/**
 * Geographic utility functions for school proximity and coastal zone analysis.
 *
 * Uses Turf.js for polygon operations (union, buffer, point-to-line distance)
 * and Leaflet's Haversine for point-to-point distance.
 */
import L from 'leaflet';
import * as turf from '@turf/turf';
import {
  SchoolData,
  NearestNeighborResult,
  CoastalResult,
  DistanceFromSelected,
  getSchoolLevel,
} from './types';

// ---------------------------------------------------------------------------
// 1. Coastline extraction — merge region polygons into island outlines
// ---------------------------------------------------------------------------

/**
 * Merge all region GeoJSON features into a single polygon via turf.union().
 * Internal boundaries dissolve; the exterior ring = coastline.
 */
export function extractCoastlinePolygon(
  regionsGeoJSON: GeoJSON.FeatureCollection
): GeoJSON.Feature | null {
  if (!regionsGeoJSON?.features?.length) return null;

  try {
    const fc = turf.featureCollection(
      regionsGeoJSON.features.map((f: any) => f)
    );

    // Iteratively union all features
    let merged: any = fc.features[0];
    for (let i = 1; i < fc.features.length; i++) {
      const result = turf.union(turf.featureCollection([merged, fc.features[i]]));
      if (result) merged = result;
    }
    return merged as GeoJSON.Feature;
  } catch (err) {
    console.error('Failed to extract coastline polygon:', err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// 2. Coastal zone polygon — 5 km inward buffer ring
// ---------------------------------------------------------------------------

/**
 * Create a coastal zone ring: the area within `bufferKm` of the coastline.
 * Strategy: buffer the merged polygon inward by -bufferKm, then subtract
 * the inner polygon from the original to get the coastal ring.
 */
export function createCoastalZonePolygon(
  mergedPolygon: GeoJSON.Feature,
  bufferKm: number
): GeoJSON.Feature | null {
  if (!mergedPolygon) return null;

  try {
    const innerBuffer = turf.buffer(mergedPolygon as any, -bufferKm, {
      units: 'kilometers',
    });

    if (!innerBuffer) {
      // Island is narrower than 2 × bufferKm — entire island is coastal
      return mergedPolygon;
    }

    const coastalRing = turf.difference(
      turf.featureCollection([mergedPolygon as any, innerBuffer as any])
    );
    return (coastalRing as GeoJSON.Feature) || mergedPolygon;
  } catch (err) {
    console.error('Failed to create coastal zone polygon:', err);
    // Fallback: return the full island as coastal zone
    return mergedPolygon;
  }
}

// ---------------------------------------------------------------------------
// 3. Extract coastline as LineString(s) for distance measurement
// ---------------------------------------------------------------------------

/**
 * Extract exterior ring(s) from a (Multi)Polygon as a MultiLineString
 * for use with turf.nearestPointOnLine().
 */
function extractExteriorRings(
  polygon: GeoJSON.Feature
): GeoJSON.Feature<GeoJSON.MultiLineString> | null {
  try {
    const coords: number[][][] = [];
    const geom = polygon.geometry;

    if (geom.type === 'Polygon') {
      coords.push((geom as GeoJSON.Polygon).coordinates[0]);
    } else if (geom.type === 'MultiPolygon') {
      for (const poly of (geom as GeoJSON.MultiPolygon).coordinates) {
        coords.push(poly[0]); // exterior ring of each polygon
      }
    }

    if (coords.length === 0) return null;
    return turf.multiLineString(coords) as unknown as GeoJSON.Feature<GeoJSON.MultiLineString>;
  } catch (err) {
    console.error('Failed to extract exterior rings:', err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// 4. Nearest-neighbor distance for each GPS school
// ---------------------------------------------------------------------------

/**
 * O(n^2) nearest-neighbor computation using Leaflet Haversine.
 * Returns Map<school_id, NearestNeighborResult>.
 */
export function computeNearestNeighbors(
  gpsSchools: SchoolData[]
): Map<string, NearestNeighborResult> {
  const results = new Map<string, NearestNeighborResult>();
  if (gpsSchools.length < 2) return results;

  // Pre-create LatLng objects for performance
  const latLngs = gpsSchools.map((s) => L.latLng(s.latitude!, s.longitude!));

  for (let i = 0; i < gpsSchools.length; i++) {
    let minDist = Infinity;
    let nearestIdx = -1;

    for (let j = 0; j < gpsSchools.length; j++) {
      if (i === j) continue;
      const dist = latLngs[i].distanceTo(latLngs[j]);
      if (dist < minDist) {
        minDist = dist;
        nearestIdx = j;
      }
    }

    if (nearestIdx >= 0) {
      results.set(gpsSchools[i].school_id, {
        schoolId: gpsSchools[i].school_id,
        nearestSchoolId: gpsSchools[nearestIdx].school_id,
        nearestSchoolName: gpsSchools[nearestIdx].school_name,
        distanceKm: parseFloat((minDist / 1000).toFixed(2)),
      });
    }
  }

  return results;
}

// ---------------------------------------------------------------------------
// 5. Coastal distance for each GPS school
// ---------------------------------------------------------------------------

/**
 * Compute distance from each GPS school to the nearest coastline point.
 * Uses turf.nearestPointOnLine() against the merged polygon's exterior ring.
 */
export function computeCoastalDistances(
  gpsSchools: SchoolData[],
  coastlinePolygon: GeoJSON.Feature,
  thresholdKm: number
): Map<string, CoastalResult> {
  const results = new Map<string, CoastalResult>();
  if (!coastlinePolygon || gpsSchools.length === 0) return results;

  const coastline = extractExteriorRings(coastlinePolygon);
  if (!coastline) return results;

  // For each line in the MultiLineString, we need to check distance individually
  // and take the minimum across all rings
  const lineCoords = coastline.geometry.coordinates;

  for (const school of gpsSchools) {
    if (school.latitude === null || school.longitude === null) continue;

    const pt = turf.point([school.longitude, school.latitude]);
    let minDist = Infinity;

    for (const ringCoords of lineCoords) {
      try {
        const line = turf.lineString(ringCoords);
        const nearest = turf.nearestPointOnLine(line, pt, { units: 'kilometers' });
        const dist = nearest.properties.dist ?? Infinity;
        if (dist < minDist) minDist = dist;
      } catch {
        // Skip invalid rings
      }
    }

    const distKm = parseFloat(minDist.toFixed(2));
    results.set(school.school_id, {
      schoolId: school.school_id,
      distanceToCoastKm: distKm,
      isCoastalZone: distKm <= thresholdKm,
    });
  }

  return results;
}

// ---------------------------------------------------------------------------
// 6. Interactive: distances from one selected school to all others
// ---------------------------------------------------------------------------

/**
 * Compute distances from a selected school to all other GPS schools.
 * Returns sorted array (nearest first), optionally limited to `maxResults`.
 */
export function computeDistancesFromSchool(
  selectedSchool: SchoolData,
  allGpsSchools: SchoolData[],
  maxResults = 50
): DistanceFromSelected[] {
  if (!selectedSchool.latitude || !selectedSchool.longitude) return [];

  const origin = L.latLng(selectedSchool.latitude, selectedSchool.longitude);
  const results: DistanceFromSelected[] = [];

  for (const school of allGpsSchools) {
    if (school.school_id === selectedSchool.school_id) continue;
    if (school.latitude === null || school.longitude === null) continue;

    const dist = origin.distanceTo(L.latLng(school.latitude, school.longitude));
    results.push({
      schoolId: school.school_id,
      schoolName: school.school_name,
      distanceKm: parseFloat((dist / 1000).toFixed(2)),
      level: getSchoolLevel(school),
      enrollment: school.current_enrollment,
      latitude: school.latitude,
      longitude: school.longitude,
    });
  }

  results.sort((a, b) => a.distanceKm - b.distanceKm);
  return maxResults ? results.slice(0, maxResults) : results;
}
